/**
 * Type definitions for FCC License Manager frontend
 */

// ===== User and Authentication Types =====

export interface User {
  id: number
  email: string
  full_name: string | null
  phone: string | null
  is_active: boolean
  is_superuser: boolean
  force_password_change: boolean
  roles: string[]
  created_at: string
  last_login: string | null
  locked_until: string | null
}

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  requires_mfa: boolean
  requires_mfa_setup?: boolean
  session_id: string | null
  message: string
  /** Set only when login completed without an MFA step (per-user MFA exemption). */
  access_token?: string | null
  token_type?: string | null
  expires_in?: number | null
}

export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
}

export interface SessionCheckResponse {
  authenticated: boolean
  user: {
    id: number
    email: string
    full_name: string | null
    is_superuser: boolean
  }
  session: {
    created_at: string
    last_activity_at: string
    expires_at: string
    inactivity_expires_at: string
  }
}

export interface MFAVerifyRequest {
  session_id: string
  code: string
  remember_device?: boolean
  device_name?: string
}

export interface TrustedDevice {
  id: number
  device_name: string
  created_at: string
  expires_at: string
  last_seen_at: string
  created_from_ip: string | null
}

export interface LoginPolicy {
  mode: 'open' | 'ip_only' | 'ip_or_trusted_device'
  allowed_cidrs: string[]
  /** Display-only names for CIDR entries: { "64.187.69.30/32": "Cheyenne office" } */
  cidr_labels?: Record<string, string>
  /** CIDRs whose logins skip the MFA prompt for this user (admin-only to change) */
  mfa_exempt_cidrs?: string[]
  /** False = this user may sign in with a password only (admin-only to change) */
  mfa_required?: boolean
}

/** Self-service update — deliberately cannot change the MFA relaxations. */
export interface LoginPolicyUpdate {
  mode: 'open' | 'ip_only' | 'ip_or_trusted_device'
  allowed_cidrs: string[]
  cidr_labels?: Record<string, string>
}

/** Admin update — IP access gate plus the per-user MFA relaxations. */
export interface AdminLoginPolicyUpdate extends LoginPolicyUpdate {
  mfa_exempt_cidrs: string[]
  mfa_required: boolean
}

/** An address a user has actually signed in from, with current policy coverage. */
export interface RecentLoginIp {
  ip_address: string
  last_seen: string
  login_count: number
  in_allowed_cidrs: boolean
  in_mfa_exempt_cidrs: boolean
}

export interface AuditLogEntry {
  id: number
  timestamp: string
  event_type: string
  user_id: number | null
  user_email: string | null
  ip_address: string | null
  action: string | null
  resource_type: string | null
  resource_id: string | null
  success: boolean
  error_message: string | null
  details: Record<string, unknown> | null
  /** A call sign, client or lead name for the resource, when it has one. */
  resource_label?: string | null
  /** In-app path to the resource's page, when it has one. */
  resource_link?: string | null
  /** Set once an admin has marked this flagged entry reviewed. */
  reviewed_at?: string | null
  reviewed_by_name?: string | null
  review_note?: string | null
}

export interface AuditLogPage {
  items: AuditLogEntry[]
  total: number
  page: number
  page_size: number
}

export interface ActivityEntry {
  id: number
  timestamp: string
  event_type: string
  action: string | null
  ip_address: string | null
  success: boolean
  details: Record<string, unknown> | null
}

export interface MFAEnrollRequest {
  device_label: string
  current_password: string
  mfa_code?: string
  /** Passkey re-auth token (from /auth/reauth/passkey/*); satisfies the second factor. */
  reauth_token?: string
}

export interface ReauthData {
  current_password: string
  mfa_code?: string
  /** Passkey re-auth token (from /auth/reauth/passkey/*); satisfies the second factor. */
  reauth_token?: string
}

export interface PasswordChangeRequest {
  current_password: string
  new_password: string
  mfa_code?: string
  /** Passkey re-auth token (from /auth/reauth/passkey/*); satisfies the second factor. */
  reauth_token?: string
}

export interface MFADevice {
  id: number
  label: string
  type: string
  verified: boolean
  /** False = soft-deleted; only returned when listing with include_inactive. */
  is_active?: boolean
  last_used_at: string | null
  use_count: number
  created_at: string
}

export interface UserSession {
  id: number
  ip_address: string | null
  user_agent: string | null
  created_at: string
  last_activity_at: string
  expires_at: string
  is_current: boolean
}

export interface MFAEnrollResponse {
  secret: string
  qr_uri: string
  backup_codes: string[]
  device_id: number
}

// ===== User Management Types =====

export interface UserCreate {
  email: string
  full_name: string
  phone?: string
  // Omit to send a set-password invite email instead of setting a password now.
  password?: string
  roles: string[]
}

export interface UserUpdateAdmin {
  full_name?: string
  phone?: string
  is_active?: boolean
  roles?: string[]
  force_password_change?: boolean
}

export interface ProfileUpdate {
  full_name?: string
  phone?: string
}

export interface UserListResponse {
  users: User[]
  total: number
  page: number
  limit: number
}

export interface PasswordResetRequest {
  new_password: string
}

// ===== FCC License Types =====

export interface FCCLicense {
  id: number
  unique_system_identifier: number
  call_sign: string
  license_status: string
  radio_service_code: string
  grant_date: string | null
  expired_date: string | null
  last_action_date: string | null
  entity: FCCEntity | null
  has_pending_application?: boolean
  // Non-null when this license has dropped out of the current FCC dump (reconciliation)
  purged_at?: string | null
}

export interface FCCEntity {
  id: number
  entity_type: string | null
  entity_type_label: string | null
  entity_name: string | null
  first_name: string | null
  middle_initial: string | null
  last_name: string | null
  suffix: string | null
  phone: string | null
  fax: string | null
  email: string | null
  street_address: string | null
  po_box: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  attention_line: string | null
  frn: string | null
}

export interface FCCFrequency {
  id: number
  location_number: number | null
  antenna_number: number | null
  frequency: number | null
  frequency_upper_band: number | null
  class_station_code: string | null
  emission_code: string | null
  transmit_receive_code: string | null
  power_output: number | null
  power_erp: number | null
  units_of_work: number | null
  date_first_used: string | null
  status_code: string | null
}

export interface FCCLocation {
  id: number
  location_number: number
  location_type_code: string | null
  location_address: string | null
  location_city: string | null
  location_county: string | null
  location_state: string | null
  location_name: string | null
  lat_degrees: number | null
  lat_minutes: number | null
  lat_seconds: number | null
  lat_direction: string | null
  long_degrees: number | null
  long_minutes: number | null
  long_seconds: number | null
  long_direction: string | null
  ground_elevation: number | null
  tower_registration_number: string | null
  height_of_support_structure: number | null
  radius_of_operation: number | null
  area_of_operation_code: string | null
}

// ===== Reference Copy Types (FCC 601-ULSHS1) =====

export interface FCCAntenna {
  id: number
  location_number: number | null
  antenna_number: number | null
  status_code: string | null
  aat_meters: number | null
  polarization: string | null
  gain: number | null
  azimuth: number | null
  height_to_tip_meters: number | null
  description: string | null
}

export interface FCCEmission {
  id: number
  location_number: number | null
  antenna_number: number | null
  frequency_assigned: number | null
  emission_designator: string | null
  frequency_sequence: number | null
  emission_sequence: number | null
}

export interface FCCBuildout {
  id: number
  location_number: number | null
  antenna_number: number | null
  frequency_assigned: number | null
  frequency_number: number | null
  buildout_deadline: string | null
  buildout_date: string | null
  discontinuance_date: string | null
}

export interface FCCControlPoint {
  id: number
  control_point_number: number | null
  address: string | null
  city: string | null
  county: string | null
  state: string | null
  phone: string | null
}

export interface FCCHistoryEntry {
  id: number
  log_date: string | null
  action_code: string | null
  uls_file_number: string | null
}

export interface ConditionItem {
  code: string
  text: string
}

export interface ReferenceConditions {
  boilerplate: string[]
  waivers: ConditionItem[]
  specific_codes: string[]
}

export interface ReferenceCopy {
  call_sign: string
  license_status: string
  status_description: string
  radio_service_code: string
  radio_service_label: string | null
  regulatory_status: string | null
  issuing_bureau: string | null
  grant_date: string | null
  effective_date: string | null
  expired_date: string | null
  cancellation_date: string | null
  last_action_date: string | null
  uls_file_number: string | null
  ebf_number: string | null
  frn: string | null
  print_date: string
  company_name: string | null
  company_website: string | null
  company_logo_url: string | null
  has_company_logo: boolean
  licensee: FCCEntity | null
  entities: FCCEntity[]
  locations: FCCLocation[]
  antennas: FCCAntenna[]
  frequencies: FCCFrequency[]
  emissions: FCCEmission[]
  buildouts: FCCBuildout[]
  control_points: FCCControlPoint[]
  conditions: ReferenceConditions
  associated_call_signs: string[]
  history: FCCHistoryEntry[]
}

// ===== Search and Pagination Types =====

/**
 * How much of a field the search term has to match.
 *
 * `starts` matches from the start of a word and is what runs while you type — it is
 * served by an index, in milliseconds. `anywhere` matches inside words, cannot use an
 * index, and is what the Search button runs. Absent means `starts`: the cheap one, so
 * nothing can buy the expensive query by omission.
 */
export type SearchMode = 'starts' | 'anywhere'

export interface LicenseSearchParams {
  /**
   * The one search box: call sign, licensee or contact name, city, state, FRN, e-mail
   * or phone. The server routes it to only the fields the term could match, so a word
   * never triggers a phone scan. AND-ed with every other filter here.
   */
  search?: string
  search_mode?: SearchMode
  call_sign?: string
  /**
   * The LICENSEE's name, and separately the filing agent's.
   *
   * FCC ships several entity rows per license — the licensee, and on ~76% of them a
   * licensee CONTACT (the agent who actually handles renewals). These are two
   * independent AND-ed filters, so setting both asks "held by this one, filed by that
   * one" — a question the single name box plus a licensee/contact selector could not
   * express. That selector was retired on 2026-08-15; the one `search` box matches
   * both parties without asking.
   */
  licensee_name?: string
  contact_name?: string
  /** Email of ANY party on the license (partial). Not narrowed by name_scope. */
  email?: string
  /** Phone of ANY party on the license (partial). Not narrowed by name_scope. */
  phone?: string
  state?: string
  service_code?: string
  expiration_start?: string
  expiration_end?: string
  /**
   * Named relative range ("next_90d"), resolved by the server on every run.
   *
   * This is the field that makes a saved search keep meaning "the next 90 days"
   * instead of freezing into the two dates it happened to produce the day it was
   * saved. When set, it overrides expiration_start/end — those are still sent so
   * the UI has something to display. Absent or "custom" = the dates are literal.
   */
  expiration_preset?: string
  status?: string
  lat?: number
  lon?: number
  radius_miles?: number
  grant_date_start?: string
  grant_date_end?: string
  /** Named relative grant range ("granted_last_90d"). See expiration_preset. */
  grant_date_preset?: string
  city?: string
  zip_code?: string
  frn?: string
  /**
   * A frequency the license is authorised on, in MHz, and how wide a window around
   * it counts as a match.
   *
   * Two fields for one question, because the stored value has five decimals
   * (462.56250) and the number a technician reads off a radio has three (462.563).
   * An equality test on the typed number finds nothing, so the server searches
   * `frequency_mhz ± frequency_tolerance_khz` instead. The tolerance defaults to
   * 1 kHz server-side — wide enough for that rounding, narrow enough never to reach
   * the adjacent channel — and widening it turns the same control into a band sweep.
   *
   * `frequency_tolerance_khz` must never be sent on its own: half the filter
   * describes nothing, and the server answers 422 rather than running unfiltered.
   *
   * `include_band_grants` widens the question from "who is ON this channel" to
   * "who MAY transmit here". Off by default, because on real data the second
   * buries the first: a search for 152.480 returns 90 licenses holding an
   * assignment on that channel and 194 more whose only claim is a blanket
   * 150–174 MHz authorisation. When it is on, those matches come back in
   * `matched_bands` rather than `matched_frequencies`, so the two never blur.
   */
  frequency_mhz?: number
  frequency_tolerance_khz?: number
  include_band_grants?: boolean
  /**
   * Do Not Contact handling. 'any' (default) returns everything; 'exclude' is the
   * working list; 'only' audits what is suppressed without leaving the search page.
   *
   * Matches on call sign, entity name OR usi — the same license-shaped types that
   * suppress a send — so 'exclude' cannot leave in somebody the send path would
   * refuse. The badge uses the same predicate, so screen and filter always agree.
   */
  dnc_status?: 'any' | 'exclude' | 'only'
  /**
   * How tightly a frequency match must agree with the radius match, when both are
   * used. Defaults to 'same_site' server-side.
   */
  frequency_site_correlation?: 'any' | 'same_site' | 'same_site_known'
  /**
   * Fixed transmitters (base stations, repeaters, control/operational fixed — FB/FX/LR
   * classes) or mobile units (handhelds and vehicles — MO/MR). Defaults to 'any'.
   *
   * When a frequency search is active this describes the assignment that MATCHED, not
   * merely the license: a license whose matched channel is mobile does not qualify as
   * "fixed" because of some unrelated fixed assignment elsewhere.
   */
  station_class?: 'any' | 'fixed' | 'mobile'
  /**
   * Only licenses with an open FCC construction obligation due within this many days,
   * or missed within the last year. Omitted = off. A day count, so a saved search
   * keeps meaning "due in the next 90 days" every day it runs.
   */
  construction_within_days?: number
  page?: number
  limit?: number
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
}

/** One matched (frequency, site) pair, and what we can say about where it is. */
export interface MatchedSite {
  frequency: number
  location_number: number | null
  /** Distance in miles, or null when the site cannot be placed — see `reason`. */
  miles: number | null
  /** 'at_site' | 'elsewhere' | 'site_unplaceable' | 'site_missing' */
  reason: string
  where: string | null
  station_class: string | null
}

export interface LicenseListResponse {
  licenses: FCCLicense[]
  total: number
  page: number
  limit: number
  /** Per match: which site the matched frequency is on, how far, and why if unplaceable. */
  matched_sites?: Record<number, MatchedSite[]>
  do_not_renew_usis: number[]           // USIs flagged do_not_renew by any linked client
  lead_usis: number[]                    // USIs already tracked as an active lead
  client_usis: number[]                  // USIs already linked to an active client
  lead_usi_to_id: Record<number, number> // USI → tracked lead.id (for links)
  client_usi_to_id: Record<number, number> // USI → client.id (for links)
  dnc_call_signs: string[]               // Call signs on the Do Not Contact list
  pending_renewal_call_signs: string[]   // Call signs with a pending FCC renewal application
  // Why each row is in these results, for the filters whose answer is not a column
  // of the license itself. Empty when that filter was not used. A license holds
  // many frequencies and many sites, so these describe the MATCH, not the license.
  matched_frequencies: Record<number, number[]>  // USI → the frequencies that matched, sorted
  // Band grants that merely SPAN the window, as [low, high] pairs. Kept apart
  // from the frequencies above: "on 152.4800" and "authorised anywhere in
  // 150.0000-174.0000" are different answers. Only when the search asked for them.
  matched_bands: Record<number, number[][]>
  matched_distance_miles: Record<number, number> // USI → miles to its nearest located transmitter
}

// ===== Do Not Contact Types =====

export interface DoNotContactEntry {
  id: number
  match_type: string
  value: string
  reason: string | null
  created_by_user_id: number | null
  created_at: string
  /**
   * The removal half. Null on an active entry — which is how the history view tells a
   * live suppression from a lifted one without a second field meaning the same thing.
   *
   * Also null on entries removed before 2026-09-21: the trail was added then and
   * deliberately not backfilled, because inventing an actor for a removal nobody can
   * evidence is worse than the gap.
   */
  deleted_at?: string | null
  deleted_by_user_id?: number | null
  removal_reason?: string | null
}

export interface DoNotContactListResponse {
  total: number
  items: DoNotContactEntry[]
}

/**
 * What a DNC entry is matched on. Mirrors the API's `match_type` pattern.
 *
 * The first three are license-shaped and are resolved through the record being
 * contacted. `email` names the recipient directly and is the only one that can
 * suppress a send to an address typed by hand.
 */
export type DncMatchType = 'call_sign' | 'entity_name' | 'fcc_usi' | 'email'

/** Sortable columns on the DNC list. Mirrors DNC_SORTS in crm.py. */
export type DncSortKey = 'created_at' | 'value' | 'match_type'

// ===== SMTP Settings =====

export interface SmtpSettings {
  host: string
  port: number
  from_name: string
  from_email: string
  username: string | null
  password: string | null
  use_tls: boolean
  use_starttls: boolean
  enabled: boolean
  last_test_at?: string | null
  last_test_result?: string | null
  updated_at?: string | null
}

// ===== FCC Import Log =====

export interface ImportLog {
  id: number
  import_type: string
  file_name: string
  file_url: string | null
  file_size_bytes: number | null
  file_hash: string | null
  download_date: string | null
  import_started_at: string | null
  import_completed_at: string | null
  duration_seconds: number | null
  records_processed: number | null
  records_inserted: number | null
  records_updated: number | null
  records_skipped: number | null
  records_errors: number | null
  status: 'running' | 'completed' | 'failed' | 'interrupted'
  error_message: string | null
  imported_by: string | null
  current_phase: string | null
  download_bytes_received: number | null
  download_bytes_total: number | null
}

// ===== FCC Cache Status =====

export interface CacheBundleInfo {
  filename: string
  size_bytes: number
  downloaded_at: string | null
  /** Last time a freshness check confirmed FCC still serves this exact file. */
  verified_at?: string | null
  age_days: number
  /** Countdown to eviction — null for retained files that never expire. */
  expires_in_days: number | null
  /** True when the file is kept indefinitely (the 4 weekly bundles). */
  retained?: boolean
  url?: string | null
  etag?: string | null
  /** FCC's Last-Modified for this copy (the conditional-GET validator). */
  last_modified?: string | null
}

export interface CacheStatus {
  bundles: Record<string, CacheBundleInfo | null>
  /** Canonical FCC URL per bundle key — present even for keys with nothing cached. */
  default_urls: Record<string, string | null>
  total_size_bytes: number
}

/** State of a background cache download / freshness check for one bundle. */
export interface CacheJobStatus {
  bundle_key: string
  operation: 'load_url' | 'refresh'
  status: 'running' | 'completed' | 'failed'
  url?: string | null
  result?: 'downloaded' | 'unchanged' | null
  message?: string | null
  started_at?: string | null
  finished_at?: string | null
}

// ===== Saved Searches =====

export interface SavedSearch {
  id: number
  name: string
  search_params: LicenseSearchParams
  created_at: string
  watch_enabled: boolean
  last_checked_at: string | null
  new_match_count: number
  /** Contribute this search's matches to the team's auto-discovered leads. */
  feeds_leads: boolean
}

export interface SavedSearchListResponse {
  searches: SavedSearch[]
  total: number
}

export interface WatchMatchesResponse {
  search_id: number
  search_name: string
  new_matches: FCCLicense[]
  total_new: number
  total_current: number
  checked_at: string
}

export interface WatchSummaryResponse {
  watched_count: number
  total_new_matches: number
  searches_with_new: number
}

// ===== License Change Log =====

export interface FCCLicenseChangeLogEntry {
  id: number
  unique_system_identifier: number
  call_sign: string | null
  change_type: 'entity_name' | 'frn' | 'both'
  old_entity_name: string | null
  new_entity_name: string | null
  old_frn: string | null
  new_frn: string | null
  detected_at: string
  import_log_id: number | null
  reviewed: boolean
  reviewed_at: string | null
  reviewed_by_user_id: number | null
  /** Null on entries reviewed before attribution was recorded — show a bare "Reviewed". */
  reviewed_by_name: string | null
}

/** Sortable columns on the change log. Mirrors CHANGE_LOG_SORTS in licenses.py. */
export type ChangeLogSortKey = 'detected_at' | 'call_sign' | 'reviewed'

/** Filters a bulk review may target. Mirrors ChangeLogFilters in licenses.py. */
export interface ChangeLogFilters {
  search?: string
  reviewed?: boolean
  change_type?: 'entity_name' | 'frn' | 'both'
  state?: string
  detected_within_days?: number
}

export interface ChangeLogBulkReviewRequest {
  mode: 'ids' | 'filter'
  reviewed: boolean
  /** Explicit selection. Capped at 250 by the API; past that, use filter mode. */
  ids?: number[]
  filters?: ChangeLogFilters
  /** Required with `filters` — the `max_id` the list endpoint reported. */
  max_id?: number | null
}

/** Filters a bulk acknowledge may target. Mirrors ConstructionFilters in licenses.py. */
export interface ConstructionFilters {
  within_days?: number
  include_overdue?: boolean
  /**
   * Omitted rather than null for "no lower bound" — this doubles as a GET query
   * param, where a null would serialise to an empty string the API rejects.
   */
  overdue_within_days?: number
  linked_only?: boolean
  include_acknowledged?: boolean
  search?: string
  state?: string
  /** First-contact state; omitted means all. */
  contact?: ContactFilter
}

export type ContactFilter = 'all' | 'needs_contact' | 'contacted'
export type ContactMethod = 'email' | 'call' | 'in_person' | 'letter' | 'other'

export interface ConstructionBulkAcknowledgeRequest {
  mode: 'ids' | 'filter'
  acknowledged: boolean
  usis?: number[]
  filters?: ConstructionFilters
}

/** What a bulk action did. Mirrors BulkResult in app/schemas/bulk.py. */
export interface BulkResult {
  mode: 'ids' | 'filter'
  /** Rows the targeting selected. */
  matched: number
  /** Rows actually changed — lower when some were already in the target state. */
  updated: number
}

export interface FCCLicenseChangeLogListResponse {
  items: FCCLicenseChangeLogEntry[]
  total: number
  page: number
  page_size: number
  /**
   * Largest id matching the filters, across every page. Echoed back to the bulk
   * endpoint as a high-water mark so "review everything matching" cannot sweep up
   * rows an import added after the search ran. Null when nothing matched.
   */
  max_id: number | null
  /**
   * How many of the matching entries are on a license a CRM client holds. Reported in
   * the bulk-review confirmation, because clearing those also clears the ownership
   * badges shown on the Clients list and client detail pages.
   */
  client_linked: number
}

// ===== Construction Deadline Alerting =====
export interface ConstructionDeadlineItem {
  fcc_unique_system_identifier: number
  call_sign: string | null
  entity_name: string | null
  earliest_deadline: string
  open_count: number
  days_remaining: number
  status: 'overdue' | 'approaching'
  client_id: number | null
  lead_id: number | null
  assigned_to_user_id: number | null
  assigned_to_name: string | null
  acknowledged: boolean
  acknowledged_at: string | null
  filed: boolean
  filed_at: string | null
  /** 'handled' = acknowledged or filed. */
  contact_state: 'needs_contact' | 'contacted' | 'handled'
  contacted_at: string | null
  /** A ContactMethod, or 'lead' when the lead's working/waiting state says so. */
  contact_method: ContactMethod | 'lead' | null
  contacted_by_name: string | null
}

export interface ConstructionDeadlineListResponse {
  items: ConstructionDeadlineItem[]
  total: number
  approaching: number
  overdue: number
  needs_contact: number
  page: number
  page_size: number
}

export interface ConstructionAlertState {
  fcc_unique_system_identifier: number
  acknowledged: boolean
  acknowledged_at: string | null
  filed: boolean
  filed_at: string | null
}

export interface ConstructionWorksheetObligation {
  location_number: number | null
  antenna_number: number | null
  frequency_assigned: number | null
  frequency_number: number | null
  buildout_deadline: string | null
  buildout_date: string | null
  status: 'built' | 'overdue' | 'open' | 'unknown'
  location_address: string | null
  location_city: string | null
  location_county: string | null
  location_state: string | null
  coordinates: string | null
  station_class: string | null
  power_erp: number | null
  power_output: number | null
  emission_designators: string[]
}

export interface ConstructionWorksheet {
  call_sign: string
  radio_service_code: string
  radio_service_label: string | null
  grant_date: string | null
  expired_date: string | null
  uls_file_number: string | null
  frn: string | null
  licensee_name: string | null
  licensee_address: string | null
  licensee_city: string | null
  licensee_state: string | null
  licensee_zip: string | null
  print_date: string
  company_name: string | null
  company_website: string | null
  company_logo_url: string | null
  has_company_logo: boolean
  linked_client_id: number | null
  linked_client_name: string | null
  acknowledged: boolean
  acknowledged_at: string | null
  filed: boolean
  filed_at: string | null
  filed_by_name: string | null
  filing_reference: string | null
  total_count: number
  open_count: number
  overdue_count: number
  built_count: number
  obligations: ConstructionWorksheetObligation[]
}

export interface ConstructionFilingResponse {
  fcc_unique_system_identifier: number
  filed: boolean
  filed_at: string | null
  filed_by_name: string | null
  filing_reference: string | null
}

// ===== Passkey (WebAuthn) Types =====

export interface Passkey {
  id: number
  passkey_label: string
  last_used_at: string | null
  use_count: number
  created_at: string
}

export interface PasskeyRegisterBeginRequest {
  passkey_label: string
  current_password: string
  mfa_code?: string
  /** Passkey re-auth token (from /auth/reauth/passkey/*); satisfies the second factor. */
  reauth_token?: string
}

export interface PasskeyRegisterBeginResponse {
  options: Record<string, unknown>
  challenge_token: string
}

export interface PasskeyRegisterCompleteRequest {
  passkey_label: string
  challenge_token: string
  credential: Record<string, unknown>
}

export interface PasskeyAuthBeginRequest {
  email?: string
}

export interface PasskeyAuthBeginResponse {
  options: Record<string, unknown>
  challenge_token: string
}

export interface PasskeyAuthCompleteRequest {
  challenge_token: string
  credential: Record<string, unknown>
}

export interface PasskeyDeleteRequest {
  current_password: string
  mfa_code?: string
}

// ===== CRM Types =====

/**
 * Intent only: are we doing business with them. `prospect` is RETIRED — it duplicated
 * the lead's own work state and gated nothing. It stays in the union so a legacy row
 * still renders, but nothing offers it; "is there anything live here" is `Client.is_stale`.
 */
export type AccountStatus = 'active' | 'inactive' | 'prospect'
export type CommunicationType = 'call' | 'email' | 'letter' | 'in_person' | 'other'
export type CommunicationDirection = 'inbound' | 'outbound'
export type ClientPriority = 'low' | 'medium' | 'high' | 'urgent'
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent'
export type TaskStatus = 'open' | 'in_progress' | 'completed' | 'cancelled'

export interface UserBrief {
  id: number
  full_name: string | null
  email: string
}

export interface ClientLicense {
  id: number
  client_id: number
  fcc_unique_system_identifier: number
  call_sign: string | null
  notify_enabled: boolean
  do_not_renew: boolean
  added_at: string
  added_by_user_id: number | null
  // FCC snapshot (frozen at link time, refreshed on import) — survives pruning
  entity_name: string | null
  radio_service_code: string | null
  license_status: string | null
  expired_date: string | null
  snapshot_updated_at: string | null
  // False when the live FCC license row no longer exists (data is archived)
  fcc_data_current: boolean
  /**
   * Newest unreviewed FCC ownership change on this license, or null. Drives the
   * "Ownership changed" badge; the id lets an admin jump straight to the entry.
   */
  ownership_change_id: number | null
}

export interface Client {
  id: number
  entity_name: string
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  account_status: AccountStatus
  priority: ClientPriority
  notes: string | null
  assigned_to_user_id: number | null
  assigned_to: UserBrief | null
  licenses: ClientLicense[]
  created_at: string
  updated_at: string
  created_by_user_id: number | null
  last_contact_at: string | null    // most recent communication; populated in list endpoint
  open_tasks_count: number          // open+in_progress tasks; populated in list endpoint
  // Unreviewed FCC ownership changes on this client's licenses; list endpoint only
  ownership_change_count: number
  /**
   * Nothing live here: no license in force or recently lapsed, no contact logged, no
   * task open or booked, no mail queued, and the record is not new. The other axis to
   * `account_status`, which is now intent only. Computed by the backend on every read
   * (app/services/client_status.py), never stored.
   */
  is_stale: boolean
}

export interface ClientCreate {
  entity_name: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  account_status?: AccountStatus
  priority?: ClientPriority
  notes?: string
  assigned_to_user_id?: number
}

export interface ClientUpdate {
  entity_name?: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  account_status?: AccountStatus
  priority?: ClientPriority
  notes?: string
  /** null clears the assignee; omit the key to leave it unchanged. */
  assigned_to_user_id?: number | null
}

export interface ClientMatchResult {
  id: number
  entity_name: string
  account_status: AccountStatus
  license_count: number
  contact_name: string | null
  contact_email: string | null
}

export interface ClientListResponse {
  total: number
  page: number
  page_size: number
  items: Client[]
}

export interface Communication {
  id: number
  client_id: number
  communication_type: CommunicationType
  direction: CommunicationDirection
  subject: string | null
  body: string | null
  occurred_at: string
  duration_minutes: number | null
  user_id: number | null
  logged_by: UserBrief | null
  created_at: string
}

export interface CommunicationCreate {
  communication_type: CommunicationType
  direction: CommunicationDirection
  subject?: string
  body?: string
  occurred_at: string
  duration_minutes?: number
}

export interface CommunicationUpdate {
  communication_type?: CommunicationType
  direction?: CommunicationDirection
  subject?: string
  body?: string
  occurred_at?: string
  duration_minutes?: number
}

export interface CommunicationListResponse {
  total: number
  page: number
  page_size: number
  items: Communication[]
}

export interface ClientNote {
  id: number
  client_id: number
  note: string
  created_by_user_id: number | null
  created_by: UserBrief | null
  created_at: string
}

export interface Task {
  id: number
  client_id: number | null
  lead_id: number | null
  client_name: string | null
  lead_entity_name: string | null
  title: string
  description: string | null
  due_date: string | null
  due_at: string | null
  priority: TaskPriority
  status: TaskStatus
  assigned_to_user_id: number | null
  assigned_to: UserBrief | null
  notify_before_minutes: number | null
  notify_when_overdue: boolean
  last_notified_at: string | null
  completed_at: string | null
  completed_by_user_id: number | null
  created_at: string
  updated_at: string
  created_by_user_id: number | null
  deleted_at: string | null
  deleted_by_user_id: number | null
}

export interface TaskCreate {
  title: string
  description?: string
  due_date?: string
  due_at?: string
  priority?: TaskPriority
  client_id?: number
  lead_id?: number
  assigned_to_user_id?: number
  notify_before_minutes?: number
  notify_when_overdue?: boolean
}

export interface TaskUpdate {
  title?: string
  description?: string
  due_date?: string
  due_at?: string
  priority?: TaskPriority
  status?: TaskStatus
  client_id?: number
  lead_id?: number
  assigned_to_user_id?: number
  notify_before_minutes?: number
  notify_when_overdue?: boolean
}

export interface TaskListResponse {
  total: number
  page: number
  page_size: number
  items: Task[]
}

export interface Lead {
  fcc_unique_system_identifier: number
  call_sign: string
  radio_service_code: string
  license_status: string
  expired_date: string | null
  days_until_expiration: number | null
  entity_name: string | null
  city: string | null
  state: string | null
  /** Why this row is a lead: "expiring", or the name of the saved search that matched it. */
  source: string
  /** Set when this license is already an explicitly tracked lead. */
  tracked_lead_id: number | null
}

export interface LeadListResponse {
  total: number
  page: number
  page_size: number
  items: Lead[]
  /** Names of the saved searches currently feeding auto-discovery. */
  feed_searches: string[]
}

// ===== Notification Settings =====

export interface NotificationSettings {
  notification_enabled: boolean
  reminder_days_before: number[]
  send_overdue_reminders: boolean
  overdue_reminder_days_after: number[]
  company_name: string
  company_email: string | null
  company_phone: string | null
  company_logo_url: string | null
  company_website: string | null
  company_address_line1: string | null
  company_address_line2: string | null
  company_city: string | null
  company_state: string | null
  company_postal_code: string | null
  has_logo: boolean
  task_deleted_retention_days: number | null
  updated_at: string | null
  /**
   * Queued messages already past due — what switching client e-mail on would release
   * on the next 15-minute run.
   *
   * Switching it on is a pause being lifted, and a long pause holds a backlog. Jesse
   * enabled reminders and a stack of backdated messages went out at once, some to a
   * client that had since been deleted, with nothing having said anything was waiting.
   */
  pending_due_count: number
}

export interface BusinessCardTemplate {
  id: number
  name: string
  card_type: 'personal' | 'company'
  owner_user_id: number | null
  sender_name: string
  title: string | null
  email: string | null
  phone: string | null
  greeting_text: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface BusinessCardSendRequest {
  card_id: number
  to_email: string
  subject?: string
  personal_note?: string
  /**
   * Set only once the sender has been shown the Do Not Contact warning and chose
   * to continue. Omitted means "not acknowledged", which the API treats as a
   * refusal — so forgetting it fails safe.
   */
  acknowledge_dnc?: boolean
}

export interface DncMatch {
  id: number
  match_type: string
  value: string
  reason: string | null
}

export interface DncStatus {
  suppressed: boolean
  matched_on: string | null
  reason: string | null
  matches: DncMatch[]
}

export interface BusinessCardSendResponse {
  sent: boolean
  message: string
}

// ===== Email Templates =====

export interface EmailTemplateVariable {
  name: string
  description: string
}

export interface EmailTemplate {
  id: number
  template_key: string
  name: string
  description: string | null
  subject_template: string
  body_markdown: string
  available_variables: EmailTemplateVariable[] | null
  /** The well-known job this message currently does, if any. */
  role: string | null
  /** Append the sender's business card as a signature block. */
  include_signature: boolean
  /** Did this ship with the app? Provenance only — it decides which section of the
   *  admin page the message appears under, and nothing else. */
  is_system: boolean
  is_active: boolean
  created_at: string
  updated_at: string
  /** Computed server-side. Deliberately not derived from `is_system`: a built-in
   *  message can be both resettable and deletable, which is what the two
   *  reference-copy templates are. */
  can_delete: boolean
  can_reset: boolean
}

/**
 * The jobs the system sends on its own, and which a message can be assigned to.
 * Mirrors TEMPLATE_ROLES on the server.
 *
 * "Custom Message" is deliberately absent. That template is live — it wraps text
 * typed into Send Now on a client page — but nothing sends it automatically, so
 * listing it here invited configuring something that never happens.
 */
export const TEMPLATE_ROLE_LABELS: Record<string, string> = {
  renewal_reminder: 'Renewal Reminder',
  renewal_overdue: 'Overdue Notice',
}

/** One of the messages the app sends that is not editable. */
export interface SystemMessage {
  key: string
  name: string
  subject: string
  when: string
  audience: string
}

export interface EmailTemplateCreate {
  name: string
  description?: string
  subject_template: string
  body_markdown: string
  available_variables?: EmailTemplateVariable[]
  include_signature?: boolean
}

export interface EmailTemplateUpdate {
  subject_template?: string
  body_markdown?: string
  is_active?: boolean
  include_signature?: boolean
}

export interface EmailTemplatePreviewResponse {
  subject: string
  html_body: string
  text_body: string
}

// ===== Scheduled messages =====

/**
 * A message somebody can choose to send from a client or lead page.
 *
 * Thinner than {@link EmailTemplate}, and served by a different endpoint: this
 * one is readable by any member of staff, while the admin list exists to *edit*
 * templates and is admin-only. Sending is ordinary work; editing is not.
 */
/** A named slot a template expects a file for. */
export interface AttachmentSlot {
  label: string
  required: boolean
  retention_policy: 'permanent' | 'days'
  retention_days: number | null
}

export interface ComposableTemplate {
  id: number
  template_key: string
  name: string
  description: string | null
  subject_template: string
  include_signature: boolean
  /** Files this message expects. A required slot must be filled before sending. */
  attachment_slots: AttachmentSlot[]
  /** Whether unlabelled extra files may be added on the day. */
  allow_extra_attachments: boolean
}

/** A stored file, as the API describes it. */
export interface StoredFileInfo {
  id: number
  original_filename: string
  content_type: string
  byte_size: number
  source: string
  scan_status: string
  retention_policy: string
  retain_until: string | null
  /** False when the file is quarantined, unscanned, purged or missing. */
  available: boolean
  uploaded_at: string
}

/** A file to send, and which slot it fills. */
export interface MessageAttachmentRef {
  file_id: number
  slot_label?: string | null
}

export type MessageRecurrence = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly'

export type ScheduledMessageStatus =
  | 'scheduled'
  | 'paused'
  | 'sent'
  | 'failed'
  | 'cancelled'

export interface ScheduledMessage {
  id: number
  client_id: number | null
  lead_id: number | null
  fcc_usi: number | null
  to_email: string
  template_id: number
  template_name: string | null
  signature_card_id: number | null
  subject_override: string | null
  /** Naive UTC, as stored. Render it with the row's own zone. */
  send_at: string
  /** The IANA zone the sender picked the time in — not decoration: a repeat is
   *  computed on this clock so it survives a daylight-saving change. */
  send_timezone: string
  recurrence: MessageRecurrence
  recurrence_interval: number
  /** "once", "every week", "every 3 months" — composed by the server. */
  recurrence_summary: string
  recurrence_until: string | null
  next_send_at: string | null
  last_sent_at: string | null
  sent_count: number
  status: ScheduledMessageStatus
  attempts: number
  /** Also carries "not sent — on the Do Not Contact list", which is not an error. */
  last_error: string | null
  created_by_user_id: number | null
  created_at: string
  target_label: string
}

/** The parts of a message that are the same whether it goes now or later. */
export interface MessageComposition {
  client_id?: number
  lead_id?: number
  fcc_usi?: number
  to_email: string
  template_id: number
  signature_card_id?: number
  subject_override?: string
  body_override?: string
}

export interface SendMessageNowRequest extends MessageComposition {
  acknowledge_dnc?: boolean
  attachments?: MessageAttachmentRef[]
  /** Sent from Construction Deadlines: stamps that deadline as contacted once sent. */
  construction_usi?: number
}

export interface ScheduleMessageRequest extends MessageComposition {
  /** Wall-clock time the sender picked, in their own zone — never UTC. */
  send_at_local: string
  send_timezone: string
  recurrence?: MessageRecurrence
  recurrence_interval?: number
  recurrence_until_local?: string
}

export interface MessagePreviewResponse {
  subject: string
  html: string
}

export interface NotificationLog {
  id: number
  notification_type: string
  days_offset: number | null
  sent_at: string
  recipient_email: string
  call_sign: string | null
  license_expiration: string | null
  status: string
  error_message: string | null
}

// ===== User Notification Settings =====

export interface UserNotificationAddress {
  id: number
  user_id: number
  channel: string        // 'email', 'sms', 'webhook'
  address: string        // email addr, phone, URL
  label: string          // "Work Email", "Email-to-Text"
  is_active: boolean
  created_at: string
}

export interface UserNotificationPref {
  id: number
  user_id: number
  priority: 'low' | 'medium' | 'high' | 'urgent'
  channels: string[]     // e.g. ["email"]
  advance_minutes: number | null
  notify_when_overdue: boolean
}

// ===== Dashboard =====

export interface DashboardMonthlyCount {
  month: string
  month_key: string
  count: number
}

export interface DashboardStats {
  total_licenses: number
  expiring_30_days: number
  expiring_90_days: number
  expired: number
  open_leads: number
  high_priority_leads: number
  leads_converted: number
  leads_dismissed: number
  leads_by_priority: Record<string, number>
  total_clients: number
  active_clients: number
  high_priority_clients: number
  /** Clients holding a license whose FCC ownership changed and nobody has reviewed it. */
  clients_with_ownership_changes: number
  /**
   * Clients with nothing live on them. `active_clients` counts `active` only, and since
   * the status reshape essentially every client is `active` — this is what carries the
   * signal the status breakdown used to. Computed, never stored.
   */
  stale_clients: number
  clients_by_status: Record<string, number>
  clients_by_priority: Record<string, number>
  open_tasks: number
  tasks_in_progress: number
  overdue_tasks: number
  my_overdue_tasks: number
  tasks_by_priority: Record<string, number>
  expiration_trend: DashboardMonthlyCount[]
  leads_trend: DashboardMonthlyCount[]
  clients_trend: DashboardMonthlyCount[]

  /**
   * Our book, as distinct from the FCC's database.
   *
   * Everything above counts the WHOLE national dump — `total_licenses`,
   * `expiring_30_days`, `expiring_90_days`, `expired` and `expiration_trend` have no
   * join to Client at all, so they move when the FCC publishes, not when we do
   * anything. They are the lead pool, and are kept and labelled as such.
   */
  client_licenses: number
  client_expiring_30: number
  client_expiring_90: number
  /** Expiring within 90 days with NO renewal already filed. The actionable one. */
  client_renewals_outstanding: number
  /** The same, within 30 days. */
  client_renewals_urgent: number
  /** The pipeline: open leads by work_state (new / working / waiting). */
  leads_by_work_state: Record<string, number>
  /** Tasks assigned to me, regardless of due date. */
  my_open_tasks: number
  conversions_trend: DashboardMonthlyCount[]
  /** Expirations of licenses WE manage, including past months. */
  client_expiration_trend: DashboardMonthlyCount[]
}

// ===== Import Settings =====

export interface ImportSettings {
  monitored_states: string[]
  monitored_services: Record<string, string>  // {code: description}
  updated_at: string | null
  all_states: { code: string; name: string }[]
  all_services: Record<string, string>
  service_categories: Record<string, string[]>
}

// ===== Backup / Restore =====

export type EncryptionMode = 'none' | 'credentials' | 'full'

export interface BackupExportOptions {
  crm_tables?: boolean
  settings?: boolean
  saved_searches?: boolean
  users?: boolean
  notification_log?: boolean
  encryption_mode?: EncryptionMode
  passphrase?: string
}

export interface BackupInfo {
  app_version: string
  db_schema_version: string
  table_groups: Record<string, string[]>
  group_defaults: Record<string, boolean>
}

export interface BackupPreflight {
  format_version: number
  app_version: string
  db_schema_version: string
  created_at: string | null
  created_by_email: string | null
  encryption_mode: EncryptionMode
  needs_passphrase: boolean
  tables: string[]
  row_counts: Record<string, number>
  schema_mismatch: boolean
  warnings: string[]
}

export interface RestoreResult {
  mode: string
  tables_restored: number
  row_counts: Record<string, number>
  warnings: string[]
}

// ===== API Error =====

export interface APIError {
  detail: string
  field?: string
}

// ===== Role Types =====

export type UserRole = 'admin' | 'employee' | 'viewer'

// ===== Search facets =====

/** One selectable filter option, with how many licenses carry it. */
export interface FacetValue {
  code: string
  label: string
  count: number
}

/**
 * The search filters' option lists, built from the data actually imported
 * (`GET /v1/licenses/facets`) rather than hard-coded here. Every entry matches
 * at least one license.
 */
export interface LicenseFacets {
  states: FacetValue[]
  services: FacetValue[]
  statuses: FacetValue[]
  total_licenses: number
  generated_at: string | null
}

// ===== License Status Codes =====
//
// Fallback labels for rendering a code that came back on a license record —
// detail pages, result rows, lead lists. The *search filter options* no longer
// come from here; they come from /v1/licenses/facets, so a newly monitored state
// or service needs no frontend change. Keep these in step with
// backend/app/services/fcc_reference.py, which is the authoritative copy.

export const LICENSE_STATUS: Record<string, string> = {
  A: 'Active',
  C: 'Cancelled',
  E: 'Expired',
  L: 'Pending Legal Status',
  P: 'Parent Station',
  T: 'Terminated',
  X: 'Terminated by FCC',
}

// ===== Radio Service Codes =====

export const RADIO_SERVICES: Record<string, string> = {
  IG: 'Industrial/Business',
  PW: 'Public Safety',
  PA: 'Public Safety - Aero',
  YG: 'Industrial/Business - SMR',
  YW: 'Public Safety - SMR',
  SG: 'Private Carrier Paging',
  SY: 'Private Carrier Paging',
  GM: 'Commercial SMR - 800 MHz',
  GX: 'Commercial SMR - 900 MHz',
  YM: 'Trunked SMR - 800 MHz',
  YX: 'Trunked SMR - 900 MHz',
}


// ---------------------------------------------------------------------------
// Frequency coordination — search 3a
//
// A DIFFERENT grain from every other search here: one row per transmitter
// assignment (license × site × antenna × frequency), not one per license. See
// backend/app/services/site_search.py for why it is its own query.
// ---------------------------------------------------------------------------

/** Why a row is in the result. Rendered as a visible chip, never a footnote. */
export type CoordinationBasis = 'fixed_site' | 'mobile_area' | 'band_grant'

export interface TransmitterRow {
  basis: CoordinationBasis
  unique_system_identifier: number
  call_sign: string | null
  licensee_name: string | null
  radio_service_code: string | null
  license_status: string | null
  expired_date: string | null

  /** NULL on a band-grant row — such a row reports band_low/band_high instead. */
  frequency: string | null
  band_low: string | null
  band_high: string | null

  class_station_code: string | null
  transmit_receive_code: string | null
  power_output: string | null
  power_erp: string | null
  units_of_work: number | null
  emission_designators: string[]

  /** NULL for an area record, which has a centre and a radius rather than a point. */
  distance_miles: number | null
  centre_miles: number | null
  bearing_degrees: number | null
  radius_of_operation_miles: string | null

  location_number: number | null
  location_type_code: string | null
  area_of_operation_code: string | null
  location_address: string | null
  location_city: string | null
  location_county: string | null
  location_state: string | null
  ground_elevation_m: string | null
  height_of_support_structure_m: string | null
  tower_registration_number: string | null

  antenna_number: number | null
  antenna_aat_m: string | null
  antenna_gain_dbd: string | null
  antenna_azimuth: string | null
  antenna_polarization: string | null
  antenna_height_to_tip_m: string | null
}

export interface SitesNearResponse {
  rows: TransmitterRow[]
  total: number
  page: number
  page_size: number
  radius_miles: number
  /** Over the WHOLE circle, not the page — always shown, even for a collapsed section. */
  counts_by_basis: Record<CoordinationBasis, number>
  /** What the "active licenses only" rule removed. Disclosed, not dropped. */
  excluded_inactive: number
  /** What this search structurally cannot see. Render it; never collapse it. */
  coverage_gaps: string[]
  development_warning: string
}
